import {toast} from '@wandb/components';
import type {WbAgentClient} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import {publishWBAgentCrossTabEvent} from '../crossTabSync';
import {queueKey, useQueueStore} from '../queue/store';
import {
  beginDelete,
  deleteThread as deleteThreadAction,
  endDelete,
} from '../store/actions';
import type {AgentChatDispatch, AgentChatStore} from '../store/context';

export type DeleteThreadParams = {
  client: WbAgentClient;
  dispatch: AgentChatDispatch;
  store: AgentChatStore;
  threadId: string;
};

/**
 * deleteThread performs an optimistic delete. The row vanishes from
 * the chat history immediately; on failure it reappears and a toast
 * fires. Duplicate calls while a delete is in flight are no-ops.
 */
export async function deleteThread({
  client,
  dispatch,
  store,
  threadId,
}: DeleteThreadParams): Promise<void> {
  const state = store.getState();
  if (
    !state.threads[threadId] ||
    state.pendingDeletes.has(threadId) ||
    state.deletedThreadIds.has(threadId)
  ) {
    return;
  }

  dispatch(beginDelete(threadId));
  const rootTurnId = state.threads[threadId].rootTurnId ?? threadId;

  try {
    await client.post<void>(`/turns/${threadId}/archive`);
    dispatch(deleteThreadAction(threadId));
    useQueueStore.getState().clear(queueKey(rootTurnId));
    publishWBAgentCrossTabEvent({type: 'thread-deleted', rootTurnId});
    dispatch(endDelete(threadId));
  } catch {
    dispatch(endDelete(threadId));
    toast.error('Failed to delete chat.');
  }
}
